import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, RefreshCw, AlertTriangle, Check, Flag, Pencil,
    Film, Camera, Mic2, ClipboardList, ShieldCheck, Clock, Copy,
    ChevronRight, ChevronDown, Wand2, Volume2, VolumeX, Music2,
    Play as PlayIcon, Sparkles, CheckCircle2, AlertCircle,
} from "lucide-react";
import { SceneMenu } from "../components/project/SceneMenu";
import { CostConfirm } from "../components/project/CostConfirm";
import { ScenePreviewDialog } from "../components/project/ScenePreviewDialog";
import { AiFixPanel } from "../components/project/AiFixPanel";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../lib/auth-context";

const SCENE_TYPE_LABEL = {
    A_TRAINER: "Trainer",
    B_RUNWAY_CINEMATIC: "Cinematic clip",
    C_RUNWAY_REFERENCE_MOTION: "Image → motion",
    D_TECHNICAL_LOCKED: "Technical (locked)",
    E_MOTION_GRAPHICS: "Motion graphics",
    F_SPLIT_TRAINER_TECHNICAL: "Split trainer + technical",
    G_QUIZ_INTERACTION: "Quiz",
};
const PRESENCE_LABEL = {
    FULL_SCREEN: "Full screen",
    HALF_SCREEN: "Half screen",
    PICTURE_IN_PICTURE: "Picture-in-picture",
    VOICE_ONLY: "Voice only",
    NO_TRAINER: "No trainer",
};
const RISK_LABEL = { LOW: "Easy", MEDIUM: "Check", HIGH: "Important" };
const RISK_CLASS = {
    LOW: "bg-emerald-50 text-emerald-700",
    MEDIUM: "bg-amber-50 text-amber-700",
    HIGH: "bg-red-50 text-red-700",
};
const REVIEW_CLASS = {
    UNREVIEWED: "bg-neutral-100 text-neutral-500",
    ACCEPTED: "bg-emerald-50 text-emerald-700",
    EDITED: "bg-blue-50 text-blue-700",
    FLAGGED: "bg-red-50 text-red-700",
};
import { moduleStatusLabel, moduleStatusPillClass } from "../lib/moduleStatus";
import BuildProgressCard from "../components/BuildProgressCard";
import FidelityBanner from "../components/FidelityBanner";
import { TargetVsCurrent } from "../components/TargetLengthControl";
import { displayCost, displayCurrency } from "../utils/pricing";

export default function StoryboardEditor({ embedded = false, onChanged } = {}) {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [detail, setDetail] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [scenes, setScenes] = useState([]);
    const [media, setMedia] = useState([]);
    // Phase 5.1 — per-scene busy flags for the Properties header
    // action buttons (approve/unapprove, regenerate voice, visual,
    // or the whole scene). Keyed by scene_id.
    const [sceneBusy, setSceneBusy] = useState({});
    const [steps, setSteps] = useState([]);
    const [selectedSceneId, setSelectedSceneId] = useState(() => new URLSearchParams(window.location.search).get("scene"));
    const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);
    const [collapsedChapters, setCollapsedChapters] = useState({});
    const [loading, setLoading] = useState(true);
    const [genJobId, setGenJobId] = useState(null);
    const [genStage, setGenStage] = useState(null);
    const [regenJobId, setRegenJobId] = useState(null);
    const [savingIndicator, setSavingIndicator] = useState("");
    const [dragScene, setDragScene] = useState(null);
    const [generations, setGenerations] = useState([]);
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [playGen, setPlayGen] = useState(null);
    const [genForm, setGenForm] = useState({ model: "", duration_s: 6, aspect_ratio: "1280:720", seed: "" });
    const [orgSettings, setOrgSettings] = useState(null);
    // 1A.7 — voice state
    const [voiceAssetsByScene, setVoiceAssetsByScene] = useState({}); // scene_id -> [voice_asset]
    const [voiceGenJobs, setVoiceGenJobs] = useState({}); // scene_id -> job_id
    const [voiceOutOfDate, setVoiceOutOfDate] = useState({}); // scene_id -> boolean
    const [voiceBulkOpen, setVoiceBulkOpen] = useState(false);
    const [voiceBulkPreview, setVoiceBulkPreview] = useState(null);
    const [availableVoices, setAvailableVoices] = useState([]);
    // 1A.8 — motion render state
    const [motionRendersByScene, setMotionRendersByScene] = useState({});
    const [motionJobs, setMotionJobs] = useState({});
    const [motionBulkOpen, setMotionBulkOpen] = useState(false);
    const [motionBulkPreview, setMotionBulkPreview] = useState(null);
    // Auto-fix state
    const [autoFixRunning, setAutoFixRunning] = useState(false);
    const [autoFixResult, setAutoFixResult] = useState(null);
    // Preflight state (drives silent auto-heal on load)
    const [preflight, setPreflight] = useState(null);
    const [bgAutoFixRunning, setBgAutoFixRunning] = useState(false);

    const runAutoFix = async () => {
        setAutoFixRunning(true); setAutoFixResult(null);
        try {
            const { data } = await api.post(`/modules/${id}/auto-fix`);
            setAutoFixResult(data);
            const fixed = data.fixed?.length || 0;
            const failed = data.failed?.length || 0;
            toast[failed ? "warning" : "success"](
                `Auto-fix ran: ${fixed} fixed, ${data.skipped?.length || 0} skipped${failed ? `, ${failed} failed` : ""}`,
            );
            await loadDetail();
            await runPreflight();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setAutoFixRunning(false); }
    };

    const runPreflight = useCallback(async () => {
        try {
            const { data } = await api.post(`/modules/${id}/render-preflight`);
            setPreflight(data);
            return data;
        } catch {
            return null;
        }
    }, [id]);

    const loadDetail = useCallback(async () => {
        try {
            // Phase 5.2 hotfix — fan out all five initial fetches
            // simultaneously once the module metadata lands. Previously
            // this was three serial waves (module → chapters+scenes →
            // media+steps), which cost ~3× round-trip latency. Lite
            // scenes payload now excludes the heavy approved_* +
            // beat_map blobs; the Properties panel fetches the full
            // detail on demand via /scenes/{id}.
            const { data } = await api.get(`/modules/${id}`);
            setDetail(data);
            const vid = data.current_version?.id;
            if (!vid) {
                setChapters([]); setScenes([]);
                return;
            }
            const docId = data.module.document_id;
            const docVid = data.module.latest_document_version_id;
            const [c, s, m, st] = await Promise.all([
                api.get(`/modules/${id}/versions/${vid}/chapters`),
                api.get(`/modules/${id}/versions/${vid}/scenes?fields=lite`),
                api.get(`/documents/${docId}/versions/${docVid}/media`).catch(() => ({ data: [] })),
                api.get(`/documents/${docId}/versions/${docVid}/steps`).catch(() => ({ data: [] })),
            ]);
            setChapters(c.data);
            setScenes(s.data);
            setMedia(m.data);
            setSteps(st.data);
            if (!selectedSceneId && s.data.length) setSelectedSceneId(s.data[0].id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }, [id, selectedSceneId]);

    useEffect(() => { loadDetail(); /* eslint-disable-next-line */ }, [id]);

    // Page load is read-only: preflight only. Auto-fix runs from the
    // background sweep or the explicit "Auto-fix blockers" button.
    useEffect(() => { runPreflight(); }, [id, runPreflight]);

    // Load Runway settings once (for defaults + available models)
    useEffect(() => {
        api.get("/integrations/runway").then(({ data }) => {
            setOrgSettings(data);
            setGenForm((g) => ({
                ...g,
                model: g.model || data.settings.default_premium_model_id,
                aspect_ratio: data.settings.default_aspect_ratio,
                duration_s: data.settings.default_clip_duration_s,
            }));
        }).catch(() => {});
        api.get("/voice/voices").then(({ data }) => setAvailableVoices(data)).catch(() => {});
    }, []);

    // 1A.7 voice loaders
    const loadVoiceAssets = useCallback(async (sceneId) => {
        if (!sceneId) return;
        try {
            const { data } = await api.get(`/scenes/${sceneId}/voice-assets`);
            setVoiceAssetsByScene((prev) => ({ ...prev, [sceneId]: data }));
        } catch { /* keep prev */ }
    }, []);

    const refreshVoiceOutOfDate = useCallback(async (sceneList) => {
        const targets = (sceneList || scenes).filter((s) => (s.voiceover || s.trainer_dialogue) && s.selected_voice_asset_id);
        const results = await Promise.all(targets.map(async (s) => {
            try {
                const { data } = await api.get(`/scenes/${s.id}/voice-out-of-date`);
                return [s.id, !!data.out_of_date];
            } catch { return [s.id, false]; }
        }));
        setVoiceOutOfDate(Object.fromEntries(results));
    }, [scenes]);

    // Load voice assets for the selected scene
    useEffect(() => {
        if (selectedSceneId) loadVoiceAssets(selectedSceneId);
    }, [selectedSceneId, loadVoiceAssets]);

    // Poll active voice jobs
    useEffect(() => {
        const activeSceneIds = Object.keys(voiceGenJobs);
        if (!activeSceneIds.length) return;
        const t = setInterval(async () => {
            for (const sceneId of activeSceneIds) {
                const jobId = voiceGenJobs[sceneId];
                if (!jobId) continue;
                try {
                    const { data } = await api.get(`/jobs/${jobId}`);
                    if (data.status === "COMPLETED" || data.status === "FAILED") {
                        setVoiceGenJobs((prev) => {
                            const n = { ...prev }; delete n[sceneId]; return n;
                        });
                        toast[data.status === "COMPLETED" ? "success" : "error"](
                            data.status === "COMPLETED" ? "Voice generated" : `Voice generation failed: ${data.error || ""}`,
                        );
                        await loadVoiceAssets(sceneId);
                        await loadDetail();
                    }
                } catch { /* keep polling */ }
            }
        }, 1800);
        return () => clearInterval(t);
    }, [voiceGenJobs, loadVoiceAssets, loadDetail]);

    // Compute out-of-date whenever scenes change
    useEffect(() => { refreshVoiceOutOfDate(); }, [scenes, refreshVoiceOutOfDate]);

    const generateVoice = async (sceneId, override) => {
        try {
            const { data } = await api.post(`/scenes/${sceneId}/voice-assets`, override || {});
            if (data.cached) {
                toast.success("Voice already generated (cached)");
                await loadVoiceAssets(sceneId);
                await loadDetail();
            } else {
                setVoiceGenJobs((prev) => ({ ...prev, [sceneId]: data.job_id }));
                toast.success("Generating voice…");
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const selectVoiceAsset = async (voiceAssetId, sceneId) => {
        try {
            await api.post(`/voice-assets/${voiceAssetId}/select`);
            await loadVoiceAssets(sceneId);
            await loadDetail();
            toast.success("Voice selected");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const regenerateVoice = async (voiceAssetId, sceneId) => {
        try {
            const { data } = await api.post(`/voice-assets/${voiceAssetId}/regenerate`);
            if (data.cached) {
                toast.success("Voice re-selected from cache");
                await loadVoiceAssets(sceneId);
            } else {
                setVoiceGenJobs((prev) => ({ ...prev, [sceneId]: data.job_id }));
                toast.success("Regenerating voice…");
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openVoiceBulk = async () => {
        setVoiceBulkOpen(true); setVoiceBulkPreview(null);
        try {
            const { data } = await api.post(`/modules/${id}/generate-all-voices`, { confirm: false });
            setVoiceBulkPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const confirmVoiceBulk = async () => {
        try {
            const { data } = await api.post(`/modules/${id}/generate-all-voices`, { confirm: true });
            toast.success(`Queued ${data.queued} voice generations`);
            setVoiceBulkOpen(false); setVoiceBulkPreview(null);
            // Track jobs
            const jobMap = {};
            for (const jid of data.job_ids) {
                // We can't map job_id back to scene without another call — skip live tracking, poll list.
                if (jid) jobMap[`bulk-${jid}`] = jid;
            }
            setTimeout(() => { loadDetail(); }, 2500);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ------- Motion (1A.8) -------
    const loadMotionRenders = useCallback(async (sceneId) => {
        if (!sceneId) return;
        try {
            const { data } = await api.get(`/scenes/${sceneId}/motion-renders`);
            setMotionRendersByScene((prev) => ({ ...prev, [sceneId]: data }));
        } catch { /* keep */ }
    }, []);

    useEffect(() => {
        if (selectedSceneId) loadMotionRenders(selectedSceneId);
    }, [selectedSceneId, loadMotionRenders]);

    useEffect(() => {
        const active = Object.keys(motionJobs);
        if (!active.length) return;
        const t = setInterval(async () => {
            for (const sceneId of active) {
                const jobId = motionJobs[sceneId];
                if (!jobId) continue;
                try {
                    const { data } = await api.get(`/jobs/${jobId}`);
                    if (data.status === "COMPLETED" || data.status === "FAILED") {
                        setMotionJobs((prev) => { const n = { ...prev }; delete n[sceneId]; return n; });
                        toast[data.status === "COMPLETED" ? "success" : "error"](
                            data.status === "COMPLETED" ? "Motion rendered" : `Motion render failed: ${data.error || ""}`,
                        );
                        await loadMotionRenders(sceneId);
                        await loadDetail();
                    }
                } catch { /* keep polling */ }
            }
        }, 2000);
        return () => clearInterval(t);
    }, [motionJobs, loadMotionRenders, loadDetail]);

    const renderMotion = async (sceneId, overrides) => {
        try {
            const { data } = await api.post(`/scenes/${sceneId}/motion-render`, overrides || {});
            if (data.cached) {
                toast.success("Motion already rendered (cached)");
                await loadMotionRenders(sceneId);
                await loadDetail();
            } else {
                setMotionJobs((prev) => ({ ...prev, [sceneId]: data.job_id }));
                toast.success("Rendering motion…");
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const selectMotion = async (mrId, sceneId) => {
        try {
            await api.post(`/motion-renders/${mrId}/select`);
            await loadMotionRenders(sceneId);
            await loadDetail();
            toast.success("Motion clip selected");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const openMotionBulk = async () => {
        setMotionBulkOpen(true); setMotionBulkPreview(null);
        try {
            const { data } = await api.post(`/modules/${id}/generate-all-motion`, { confirm: false });
            setMotionBulkPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const confirmMotionBulk = async () => {
        try {
            const { data } = await api.post(`/modules/${id}/generate-all-motion`, { confirm: true });
            toast.success(`Queued ${data.queued} motion renders`);
            setMotionBulkOpen(false); setMotionBulkPreview(null);
            setTimeout(() => { loadDetail(); if (selectedSceneId) loadMotionRenders(selectedSceneId); }, 4000);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Reload generations whenever selected scene changes
    const loadGenerations = useCallback(async () => {
        if (!selectedSceneId) return;
        try {
            const { data } = await api.get(`/scenes/${selectedSceneId}/generations`);
            setGenerations(data);
        } catch { setGenerations([]); }
    }, [selectedSceneId]);
    useEffect(() => { loadGenerations(); }, [loadGenerations]);
    // Poll generations while any are in flight
    useEffect(() => {
        if (!selectedSceneId) return;
        const active = generations.some((g) => ["QUEUED", "PROCESSING"].includes(g.status));
        if (!active) return;
        const t = setInterval(loadGenerations, 4000);
        return () => clearInterval(t);
    }, [generations, selectedSceneId, loadGenerations]);

    // Poll job progress
    useEffect(() => {
        if (!genJobId) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/jobs/${genJobId}`);
                setGenStage(data.progress_stage);
                if (data.status === "COMPLETED" || data.status === "FAILED") {
                    setGenJobId(null); setGenStage(null);
                    toast[data.status === "COMPLETED" ? "success" : "error"](
                        data.status === "COMPLETED" ? "Storyboard generated" : `Generation failed: ${data.error || ""}`
                    );
                    await loadDetail();
                }
            } catch { /* keep polling */ }
        }, 2000);
        return () => clearInterval(t);
    }, [genJobId, loadDetail]);

    useEffect(() => {
        if (!regenJobId) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/jobs/${regenJobId}`);
                if (data.status === "COMPLETED" || data.status === "FAILED") {
                    setRegenJobId(null);
                    toast[data.status === "COMPLETED" ? "success" : "error"](
                        data.status === "COMPLETED" ? "Scene regenerated" : `Regenerate failed: ${data.error || ""}`
                    );
                    await loadDetail();
                }
            } catch {}
        }, 1800);
        return () => clearInterval(t);
    }, [regenJobId, loadDetail]);

    const selected = useMemo(() => scenes.find((s) => s.id === selectedSceneId), [scenes, selectedSceneId]);
    const scenesByChapter = useMemo(() => {
        const map = new Map();
        for (const c of chapters) map.set(c.id, []);
        for (const s of scenes) {
            if (!map.has(s.chapter_id)) map.set(s.chapter_id, []);
            map.get(s.chapter_id).push(s);
        }
        for (const arr of map.values()) arr.sort((a, b) => a.order_index - b.order_index);
        return map;
    }, [chapters, scenes]);

    const orderedScenes = useMemo(() => {
        const out = [];
        for (const c of chapters) {
            for (const s of scenesByChapter.get(c.id) || []) out.push(s);
        }
        return out;
    }, [chapters, scenesByChapter]);

    // Cycle-6 fix: the footer "unreviewed" count previously read from
    // ``scene.review_state`` (a legacy SME reviewer field that stays
    // ``UNREVIEWED`` even after the layman approve flow). That caused
    // the stale "N unreviewed" chip after the reviewer approved every
    // scene. Wire to the same ``approval_state`` truth the rest of the
    // app uses so the footer counter matches the counter above the
    // scene list.
    const unreviewedCount = useMemo(
        () => scenes.filter((s) => (s.approval_state || "").toUpperCase() !== "APPROVED").length,
        [scenes],
    );
    // Layman-side approval counter used to gate the "Publish" affordance.
    // Reads DB-persisted ``approval_state`` on each scene so a reload
    // after per-scene approve preserves progress.
    const approvedScenesCount = useMemo(
        () => scenes.filter((s) => (s.approval_state || "").toUpperCase() === "APPROVED").length,
        [scenes],
    );
    const riskCounts = useMemo(() => scenes.reduce(
        (a, s) => { a[s.fidelity_risk] = (a[s.fidelity_risk] || 0) + 1; return a; },
        { LOW: 0, MEDIUM: 0, HIGH: 0 }
    ), [scenes]);

    const startGenerate = async () => {
        try {
            const { data } = await api.post(`/modules/${id}/generate`);
            setGenJobId(data.job_id); setGenStage("queued");
            toast.success("Generation started");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const regenerateScene = async (sid) => {
        try {
            const { data } = await api.post(`/scenes/${sid}/regenerate`);
            setRegenJobId(data.job_id);
            toast.success("Regenerating scene…");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Phase 5.1 hotfix — per-scene approval + regeneration handlers
    // wired into the Advanced Properties header (ScenePropertiesHeader).
    const withSceneBusy = async (sid, fn) => {
        setSceneBusy((prev) => ({ ...prev, [sid]: true }));
        try { await fn(); }
        finally { setSceneBusy((prev) => { const n = { ...prev }; delete n[sid]; return n; }); }
    };

    const approveScene = (sid) => withSceneBusy(sid, async () => {
        try {
            await api.post(`/scenes/${sid}/approve`);
            toast.success("Scene approved", { duration: 6000 });
            await loadDetail();
            await runPreflight();
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    });

    const unapproveScene = (sid) => withSceneBusy(sid, async () => {
        try {
            await api.post(`/scenes/${sid}/unapprove`);
            toast.success("Approval withdrawn", { duration: 6000 });
            await loadDetail();
            await runPreflight();
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    });

    const approveRemaining = async () => {
        try {
            const { data } = await api.post(`/modules/${id}/scenes/approve-all`, {});
            toast.success(`${data.approved_now} scene${data.approved_now === 1 ? "" : "s"} approved · ${data.scenes_approved} of ${data.scenes_total} approved`, { duration: 8000 });
            if (data.failed?.length) toast.error(`${data.failed.length} scene(s) couldn't be approved`);
        } catch (e) { toast.error(formatApiError(e)); }
        await loadDetail();
        await runPreflight();
        onChanged && onChanged();
    };
    const sceneChanged = async () => { await loadDetail(); onChanged && onChanged(); };

    const regenerateSceneVoice = (sid) => withSceneBusy(sid, async () => {
        try {
            const { data } = await api.post(`/scenes/${sid}/regenerate-voice`);
            if (data.cached) {
                toast.success("Voice already generated (cached)");
                await loadVoiceAssets(sid);
                await loadDetail();
            } else if (data.job_id) {
                setVoiceGenJobs((prev) => ({ ...prev, [sid]: data.job_id }));
                toast.success("Regenerating voiceover…");
            } else {
                toast.success("Voiceover queued");
            }
        } catch (e) { toast.error(formatApiError(e)); }
    });

    const regenerateSceneVisual = (sid) => withSceneBusy(sid, async () => {
        try {
            const { data } = await api.post(`/scenes/${sid}/regenerate-visual`);
            if (data.cached) {
                toast.success("Visual already rendered (cached)");
                await loadMotionRenders(sid);
                await loadDetail();
            } else if (data.job_id) {
                setMotionJobs((prev) => ({ ...prev, [sid]: data.job_id }));
                toast.success("Regenerating visual…");
            } else {
                toast.success("Visual queued");
            }
        } catch (e) { toast.error(formatApiError(e)); }
    });

    const regenerateSceneAll = (sid) => withSceneBusy(sid, async () => {
        try {
            const { data } = await api.post(`/scenes/${sid}/regenerate`);
            setRegenJobId(data.job_id);
            toast.success("Regenerating entire scene…");
        } catch (e) { toast.error(formatApiError(e)); }
    });

    // Every paid regen goes through a one-click cost confirm first.
    const [regenConfirm, setRegenConfirm] = useState(null);
    const confirmRegen = (action, label, fn) => (sid) => {
        const sc = scenes.find((s) => s.id === sid);
        setRegenConfirm({
            title: `${label} for scene ${sc?.scene_number ?? ""}?`, okLabel: label,
            description: "Runs only after you confirm.",
            estimate: { moduleId: id, action, sceneId: sid },
            onConfirm: () => fn(sid),
        });
    };


    const patchScene = async (sid, body) => {
        setSavingIndicator("Saving…");
        try {
            const { data } = await api.patch(`/scenes/${sid}`, body);
            setScenes((prev) => prev.map((s) => (s.id === sid ? data : s)));
            setSavingIndicator("Saved");
            toast.success("Saved", { duration: 1200 });
            setTimeout(() => setSavingIndicator(""), 1200);
            return true;
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (e?.response?.status === 409 && d?.violations) {
                toast.error("Fact-preservation violation: " + (d.violations[0]?.detail || "check numbers"));
            } else {
                toast.error(formatApiError(e));
            }
            setSavingIndicator("");
            return false;
        }
    };

    // Drag-and-drop: HTML5 native
    const onDragStart = (sid) => setDragScene(sid);
    const onDragOver = (e) => e.preventDefault();
    const onDropAt = async (targetSid) => {
        if (!dragScene || dragScene === targetSid) return;
        const orderIds = orderedScenes.map((s) => s.id);
        const from = orderIds.indexOf(dragScene);
        const to = orderIds.indexOf(targetSid);
        if (from < 0 || to < 0) return;
        orderIds.splice(to, 0, orderIds.splice(from, 1)[0]);
        try {
            const vid = detail.current_version.id;
            await api.post(`/modules/${id}/versions/${vid}/reorder`, { ordered_scene_ids: orderIds });
            await loadDetail();
            toast.success("Scene order saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDragScene(null); }
    };

    const doPublish = async () => {
        try {
            await api.post(`/modules/${id}/publish`);
            toast.success("Sent for approval");
            await loadDetail();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doDuplicate = async () => {
        try {
            const vid = detail.current_version.id;
            const { data } = await api.post(`/modules/${id}/versions/${vid}/duplicate`);
            toast.success(`Duplicated as version ${data.version_number}`);
            await loadDetail();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const startGeneration = async (overrides = {}) => {
        if (!selected) return;
        try {
            const body = {
                model: overrides.model || genForm.model || undefined,
                duration_s: overrides.duration_s || genForm.duration_s || undefined,
                aspect_ratio: overrides.aspect_ratio || genForm.aspect_ratio || undefined,
                seed: genForm.seed ? parseInt(genForm.seed, 10) : undefined,
            };
            await api.post(`/scenes/${selected.id}/generations`, body);
            toast.success("Generation queued");
            await loadGenerations();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (typeof d === "object" && d?.remediation) {
                toast.error(`${d.message} — ${d.remediation.join(" · ")}`);
            } else {
                toast.error(formatApiError(e));
            }
        }
    };

    const selectGeneration = async (gid) => {
        try {
            await api.post(`/scenes/${selected.id}/select-generation`, { generation_id: gid });
            toast.success("Selected");
            await loadDetail(); await loadGenerations();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const cancelGeneration = async (gid) => {
        try {
            await api.post(`/generations/${gid}/cancel`);
            toast.success("Cancelled");
            await loadGenerations();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openBulkModal = async () => {
        setBulkOpen(true);
        try {
            const { data } = await api.post(`/modules/${id}/generate-all-runway`, { confirm: false });
            setBulkPreview(data);
        } catch (e) { toast.error(formatApiError(e)); setBulkOpen(false); }
    };
    const confirmBulk = async () => {
        try {
            const { data } = await api.post(`/modules/${id}/generate-all-runway`, { confirm: true });
            toast.success(`${data.queued} generations queued (~$${displayCurrency(data.total_estimated_cost_usd)})`);
            setBulkOpen(false); setBulkPreview(null);
            await loadGenerations();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !detail) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

    const m = detail.module;
    // Storyboard-approval hard gate — every render / regen / publish
    // affordance below is wrapped in this so pre-approval modules
    // present the reviewer with a single "Review & approve" surface.
    const STORYBOARD_APPROVED = m.status === "APPROVED" || m.status === "PUBLISHED";

    return (
        <div data-testid="storyboard-editor" className="space-y-4">
            {/* Live build progress — always mounted so `data-testid=
                build-progress-card` is discoverable by tests. Collapses
                to a compact "Ready" line when state==="ready". */}
            {!embedded && <BuildProgressCard moduleId={id} isAdmin={user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN"} renderGated={!STORYBOARD_APPROVED} />}

            {/* Fidelity contract banner (Phase 5.0) — yellow for image
                fallback, red for SCRIPT_DRIFT, green pill for clean. */}
            {!embedded && <FidelityBanner
                moduleId={id}
                canRetry={user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN" || user?.role === "CONTENT_CREATOR"}
            />}

            {/* Top toolbar */}
            <div className={`flex items-start justify-between gap-4 ${embedded ? "hidden" : ""}`}>
                <div className="flex items-start gap-4">
                    <Button variant="ghost" size="sm" onClick={() => nav("/content/storyboards")} data-testid="back-to-storyboards" className="-ml-2 mt-1 text-neutral-600 hover:text-neutral-900">
                        <ArrowLeft size={14} className="mr-1" /> Storyboards
                    </Button>
                    <div>
                        <div className="text-lg font-medium text-neutral-900 leading-tight">{m.name}</div>
                        <div className="mt-0.5 flex items-center gap-3 text-[11px] text-neutral-500">
                            <span>{detail.document_title || "—"}{detail.document_revision ? ` · rev ${detail.document_revision}` : ""}</span>
                            <span>·</span>
                            <span>Trainer: {detail.trainer_name || "—"}</span>
                            {detail.current_version && (
                                <>
                                    <span>·</span>
                                    <span>v{detail.current_version.version_number}</span>
                                </>
                            )}
                            <span data-testid="module-status" title={m.status} className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${moduleStatusPillClass(m.status)}`}>
                                {moduleStatusLabel(m.status)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                    <div className="mr-2 text-right">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Duration</div>
                        <TargetVsCurrent targetMinutes={detail.target_video_minutes ?? m.target_video_minutes}
                            estimatedSeconds={detail.estimated_duration_s} testId="storyboard-target-vs-current" />
                    </div>
                    {detail.current_version && (
                        <Button size="sm" variant="outline" onClick={doDuplicate} data-testid="duplicate-version-btn">
                            <Copy size={13} className="mr-1.5" /> Duplicate version
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => nav(`/content/storyboards/${id}/fidelity`)} data-testid="btn-open-fidelity">
                        <ShieldCheck size={13} className="mr-1.5" /> Content check
                    </Button>
                    {(user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN") && STORYBOARD_APPROVED && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={runAutoFix}
                            disabled={autoFixRunning}
                            data-testid="auto-fix-blockers-btn"
                            className="border-emerald-200 text-emerald-800 hover:bg-emerald-50 hover:text-emerald-900"
                        >
                            <Sparkles size={13} className={`mr-1.5 ${autoFixRunning ? "animate-pulse" : ""}`} />
                            {autoFixRunning ? "Fixing…" : "Fix problems"}
                        </Button>
                    )}
                    {/* Hide "Regenerate storyboard" once we're waiting on
                        approval — the reviewer should approve or request
                        changes, not silently regenerate. First-time
                        "Generate storyboard" stays visible for DRAFT. */}
                    {(m.status === "DRAFT" || m.status === "STRUCTURING" || STORYBOARD_APPROVED) && (
                        <Button
                            size="sm" variant={m.status === "DRAFT" ? "default" : "outline"}
                            onClick={startGenerate}
                            disabled={!!genJobId || m.status === "STRUCTURING"}
                            data-testid="generate-btn"
                            className={m.status === "DRAFT" ? "bg-neutral-900 text-white hover:bg-neutral-800" : ""}
                        >
                            <Wand2 size={13} className="mr-1.5" />
                            {detail.current_version ? "Regenerate storyboard" : "Generate storyboard"}
                        </Button>
                    )}
                    {user?.role === "ORG_ADMIN" && STORYBOARD_APPROVED && detail.current_version && (
                        <Button size="sm" variant="outline" onClick={openBulkModal} data-testid="bulk-generate-btn">
                            <Wand2 size={13} className="mr-1.5" /> Generate all cinematic clips
                        </Button>
                    )}
                    {user?.role === "ORG_ADMIN" && STORYBOARD_APPROVED && detail.current_version && (
                        <Button size="sm" variant="outline" onClick={openVoiceBulk} data-testid="bulk-voice-btn">
                            <Mic2 size={13} className="mr-1.5" /> Generate all voices
                        </Button>
                    )}
                    {(user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN") && STORYBOARD_APPROVED && detail.current_version && (
                        <Button size="sm" variant="outline" onClick={openMotionBulk} data-testid="bulk-motion-btn">
                            <Film size={13} className="mr-1.5" /> Generate all motion
                        </Button>
                    )}
                    {detail.current_version && STORYBOARD_APPROVED && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => nav(`/content/video-studio/${id}`)}
                            data-testid="render-preview-btn"
                        >
                            <PlayIcon size={13} className="mr-1.5" /> Render preview
                        </Button>
                    )}
                    {/* "Publish storyboard" gate — visible only when every
                        scene is approved. Backend `/publish` still returns
                        409 on unapproved modules, but hiding the affordance
                        keeps the UI honest. */}
                    {user?.role === "ORG_ADMIN" && STORYBOARD_APPROVED && (
                        <Button size="sm" onClick={doPublish} data-testid="publish-btn" className="bg-neutral-900 text-white hover:bg-neutral-800">
                            Publish storyboard
                        </Button>
                    )}
                </div>
            </div>

            {/* Generation progress banner */}
            {(genJobId || m.status === "STRUCTURING") && (
                <div data-testid="generation-banner" className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-3 text-sm text-blue-900">
                    <RefreshCw size={14} className="animate-spin" />
                    <span>Generating storyboard…</span>
                    <span className="mono text-xs text-blue-700">stage: {genStage || "queued"}</span>
                </div>
            )}

            {/* Auto-fix result banner (collapsible details) */}
            {autoFixResult && (
                <details data-testid="auto-fix-result" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    <summary className="flex items-center gap-2 cursor-pointer select-none">
                        <Sparkles size={14} className="text-emerald-700" />
                        <span className="font-medium">
                            Auto-fix results ·
                            <span className="mono ml-1.5">fixed {autoFixResult.fixed?.length || 0}</span>
                            <span className="mono ml-1.5 text-emerald-700/80">skipped {autoFixResult.skipped?.length || 0}</span>
                            {autoFixResult.failed?.length ? (
                                <span className="mono ml-1.5 text-amber-800">failed {autoFixResult.failed.length}</span>
                            ) : null}
                        </span>
                    </summary>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
                        <div>
                            <div className="font-semibold text-emerald-900 mb-1">Fixed</div>
                            {autoFixResult.fixed?.length ? (
                                <ul className="space-y-1">
                                    {autoFixResult.fixed.map((f, i) => (
                                        <li key={i} className="mono text-emerald-900/90">{f.fix}</li>
                                    ))}
                                </ul>
                            ) : <div className="text-emerald-800/70">Nothing needed fixing.</div>}
                        </div>
                        <div>
                            <div className="font-semibold text-emerald-900 mb-1">Skipped</div>
                            {autoFixResult.skipped?.length ? (
                                <ul className="space-y-1">
                                    {autoFixResult.skipped.map((f, i) => (
                                        <li key={i} className="mono text-emerald-800/80">{f.fix} <span className="text-emerald-700/60">· {f.reason}</span></li>
                                    ))}
                                </ul>
                            ) : <div className="text-emerald-800/70">—</div>}
                        </div>
                        <div>
                            <div className="font-semibold text-amber-900 mb-1">Failed</div>
                            {autoFixResult.failed?.length ? (
                                <ul className="space-y-1">
                                    {autoFixResult.failed.map((f, i) => (
                                        <li key={i} className="mono text-amber-800">{f.fix} <span className="text-amber-700/70">· {f.error}</span></li>
                                    ))}
                                </ul>
                            ) : <div className="text-emerald-800/70">—</div>}
                        </div>
                    </div>
                </details>
            )}

            {/* Blockers banner — plain-English silent auto-heal on load */}
            {preflight && (preflight.issues?.length > 0 || bgAutoFixRunning) && (
                <div
                    data-testid="blockers-banner"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            {bgAutoFixRunning
                                ? <RefreshCw size={14} className="animate-spin text-amber-700" />
                                : <AlertTriangle size={14} className="text-amber-700" />}
                            <span className="font-medium">
                                {bgAutoFixRunning
                                    ? "Polishing your video…"
                                    : (preflight.issues?.[0]?.kind === "storyboard_not_approved"
                                        ? "Storyboard needs approval before building the video"
                                        : (preflight.issues?.length === 1
                                            ? "One scene still needs a moment"
                                            : `${preflight.issues.length} scenes still need a moment`))}
                            </span>
                        </div>
                    </div>
                    {!bgAutoFixRunning && preflight.issues?.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-amber-900/90" data-testid="blockers-list">
                            {preflight.issues.slice(0, 8).map((it, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <span>•</span>
                                    <span>{it.message}</span>
                                </li>
                            ))}
                            {preflight.issues.length > 8 && (
                                <li className="text-amber-700/70 text-[11px]">
                                    …and {preflight.issues.length - 8} more
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            )}

            {/* No version yet */}
            {!detail.current_version && !genJobId && (
                <div className="rounded-lg border border-dashed border-neutral-200 bg-white p-10 text-center">
                    <Film size={22} className="mx-auto mb-3 text-neutral-400" />
                    <div className="text-sm font-medium text-neutral-900">Your storyboard hasn't been built yet</div>
                    <p className="mt-1 text-sm text-neutral-500">Click "Generate storyboard" to have the AI turn your document into scenes.</p>
                </div>
            )}

            {detail.current_version && (
                <SimpleSceneList
                    scenes={scenes}
                    moduleId={id}
                    onChanged={sceneChanged}
                    onUpdateScene={patchScene}
                />
            )}
            <CostConfirm confirm={regenConfirm} onClose={() => setRegenConfirm(null)} />

            {detail.current_version && (
                <section className="rounded-lg border border-neutral-200 bg-white" data-testid="advanced-panel">
                    <OnboardingHelper />
                    <div className="border-t border-neutral-200 p-4">
                <div className="grid grid-cols-12 gap-4 h-[calc(100vh-260px)]">
                    {/* LEFT — scene list */}
                    <div className="col-span-3 rounded-lg border border-neutral-200 bg-white overflow-hidden flex flex-col">
                        <div className="border-b border-neutral-200 px-3 py-2 space-y-2" data-testid="scene-list-header">
                            <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
                                <span>Scenes</span>
                                <span data-testid="scene-list-approved-count" className="normal-case tracking-normal font-normal">{approvedScenesCount} of {scenes.length} approved</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {unreviewedCount > 0 && (
                                    <button type="button" data-testid="approve-remaining-btn" onClick={approveRemaining}
                                        className="rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-neutral-800">
                                        {approvedScenesCount === 0 ? "Approve all" : `Approve remaining ${unreviewedCount}`}
                                    </button>
                                )}
                                <label className="flex items-center gap-1 text-[11px] text-neutral-600" data-testid="needs-review-filter">
                                    <input type="checkbox" checked={onlyNeedsReview} onChange={(e) => setOnlyNeedsReview(e.target.checked)} data-testid="needs-review-filter-checkbox" />
                                    Show only needs review
                                </label>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto py-2" data-testid="scene-list">
                            {chapters.map((c) => {
                                const list = (scenesByChapter.get(c.id) || []).filter((s) => !onlyNeedsReview || (s.approval_state || "").toUpperCase() !== "APPROVED");
                                if (onlyNeedsReview && !list.length) return null;
                                const collapsed = !!collapsedChapters[c.id];
                                return (
                                    <div key={c.id} className="mb-2">
                                        <button
                                            data-testid={`chapter-toggle-${c.name}`}
                                            onClick={() => setCollapsedChapters((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                                            className="w-full px-3 py-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-neutral-500 hover:bg-neutral-50"
                                        >
                                            <span className="flex items-center gap-1.5">
                                                {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                                {c.name}
                                            </span>
                                            <span className="mono text-[10px] text-neutral-400">{list.length}</span>
                                        </button>
                                        {!collapsed && (
                                            <div className="space-y-0.5 px-2">
                                                {list.map((s) => (
                                                    <SceneRow
                                                        key={s.id}
                                                        scene={s}
                                                        selected={selectedSceneId === s.id}
                                                        onSelect={() => setSelectedSceneId(s.id)}
                                                        onDragStart={() => onDragStart(s.id)}
                                                        onDragOver={onDragOver}
                                                        onDrop={() => onDropAt(s.id)}
                                                        voiceOutOfDate={!!voiceOutOfDate[s.id]}
                                                        moduleId={id}
                                                        onChanged={sceneChanged}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* CENTER — scene preview mockup */}
                    <div className="col-span-5 rounded-lg border border-neutral-200 bg-white overflow-hidden flex flex-col">
                        <div className="border-b border-neutral-200 px-4 py-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
                            <span>Scene preview {selected ? `· ${selected.scene_number}` : ""}</span>
                            {selected && (
                                <Button size="sm" variant="outline" onClick={() => confirmRegen("redo_script", "Rewrite script", regenerateScene)(selected.id)} disabled={!!regenJobId} data-testid="regenerate-scene-btn">
                                    <RefreshCw size={12} className={`mr-1.5 ${regenJobId ? "animate-spin" : ""}`} /> Regenerate scene
                                </Button>
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto p-5" data-testid="scene-preview">
                            {selected ? (
                                <>
                                    <ScenePreview
                                        scene={selected}
                                        media={media}
                                        selectedGeneration={generations.find((g) => g.id === selected.selected_generation_id)}
                                    />
                                    <GenerationsPanel
                                        scene={selected}
                                        generations={generations}
                                        onStart={startGeneration}
                                        onSelect={selectGeneration}
                                        onCancel={cancelGeneration}
                                        onPlay={setPlayGen}
                                        settings={orgSettings?.settings}
                                    />
                                    <MotionRenderPanel
                                        scene={selected}
                                        renders={motionRendersByScene[selected.id] || []}
                                        onRender={renderMotion}
                                        onSelect={selectMotion}
                                        active={!!motionJobs[selected.id]}
                                    />
                                </>
                            ) : (
                                <div className="text-sm text-neutral-500">Select a scene from the list.</div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT — properties editor */}
                    <div className="col-span-4 rounded-lg border border-neutral-200 bg-white overflow-hidden flex flex-col">
                        <div className="border-b border-neutral-200 px-4 py-3 flex flex-col gap-2.5" data-testid="scene-properties-header">
                            <PropertiesErrorBoundary>
                            {selected ? (
                                <ScenePropertiesHeader
                                    scene={selected}
                                    canEdit={user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN" || user?.role === "CONTENT_CREATOR" || user?.role === "SME_REVIEWER"}
                                    onApprove={approveScene}
                                    onUnapprove={unapproveScene}
                                    onRegenerateVoice={confirmRegen("redo_voice", "Redo voice", regenerateSceneVoice)}
                                    onRegenerateVisual={confirmRegen("redo_visuals", "Redo visuals", regenerateSceneVisual)}
                                    onRegenerateAll={confirmRegen("redo_script", "Regenerate scene", regenerateSceneAll)}
                                    busy={!!sceneBusy[selected.id]}
                                    onPatch={patchScene}
                                />
                            ) : (
                                <div className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500">Properties</div>
                            )}
                            </PropertiesErrorBoundary>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4" data-testid="scene-properties">
                            <PropertiesErrorBoundary>
                            {selected ? (
                                <ScenePropertiesTabs
                                    scene={selected}
                                    media={media.filter((m) => m.version_id === detail.module.latest_document_version_id)}
                                    steps={steps}
                                    onPatch={patchScene}
                                    orgSettings={orgSettings}
                                    genForm={genForm}
                                    setGenForm={setGenForm}
                                    voiceAssetsForScene={voiceAssetsByScene[selected.id] || []}
                                    availableVoices={availableVoices}
                                    onGenerateVoice={generateVoice}
                                    onSelectVoice={selectVoiceAsset}
                                    onRegenerateVoice={(vaId, sid) => confirmRegen("redo_voice", "Regenerate voice", () => regenerateVoice(vaId, sid))(sid)}
                                    voiceJobActive={!!voiceGenJobs[selected.id]}
                                    outOfDate={!!voiceOutOfDate[selected.id]}
                                />
                            ) : (
                                <div className="text-sm text-neutral-500">Select a scene to edit its properties.</div>
                            )}
                            </PropertiesErrorBoundary>
                        </div>
                    </div>
                </div>
                    </div>
                </section>
            )}

            {/* Sticky footer */}
            {detail.current_version && (
                <div data-testid="editor-footer" className="sticky bottom-0 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 shadow-sm">
                    <div className="flex items-center gap-4 text-sm text-neutral-700">
                        <div><span className="mono font-medium">{unreviewedCount}</span> unreviewed</div>
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700">Easy {riskCounts.LOW}</span>
                            <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700">Check {riskCounts.MEDIUM}</span>
                            <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-700">Important {riskCounts.HIGH}</span>
                        </div>
                        {Object.values(voiceOutOfDate).some(Boolean) && (
                            <div data-testid="voices-out-of-date-count" className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                voices out of date: {Object.values(voiceOutOfDate).filter(Boolean).length}
                            </div>
                        )}
                        {m.render_status && m.render_status !== "NEVER_RENDERED" ? (
                            <div
                                data-testid="render-status-chip"
                                className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                                    m.render_status === "READY" ? "bg-emerald-50 text-emerald-700" :
                                    m.render_status === "RENDERING" ? "bg-blue-50 text-blue-700" :
                                    m.render_status === "FAILED" ? "bg-red-50 text-red-700" :
                                    "bg-amber-50 text-amber-800"
                                }`}
                            >
                                render: {m.render_status.toLowerCase()}
                            </div>
                        ) : (
                            <div
                                data-testid="render-status-chip"
                                className="inline-flex items-center rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500"
                            >
                                render: never
                            </div>
                        )}
                        <TargetVsCurrent targetMinutes={detail.target_video_minutes ?? m.target_video_minutes}
                            estimatedSeconds={detail.estimated_duration_s} testId="storyboard-header-target-vs-current" />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-neutral-500">
                        {savingIndicator && <span data-testid="save-indicator" className="mono">{savingIndicator}</span>}
                        <details className="relative">
                            <summary className="cursor-pointer rounded-md border border-neutral-200 px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-50" data-testid="preview-blockers-btn">
                                Preview blockers
                            </summary>
                            <div className="absolute bottom-full right-0 mb-2 w-80 rounded-lg border border-neutral-200 bg-white p-3 shadow-lg z-10 text-neutral-700">
                                <div className="text-sm font-medium text-neutral-900 mb-1">Fidelity check</div>
                                <p className="text-xs text-neutral-500">
                                    Review scenes marked “Important” in the Content check.
                                </p>
                            </div>
                        </details>
                    </div>
                </div>
            )}

            {/* Playback modal */}
            {playGen && (
                <div data-testid="play-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6" onClick={() => setPlayGen(null)}>
                    <div className="max-w-4xl w-full bg-neutral-950 rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-2 flex items-center justify-between text-neutral-300 text-sm">
                            <span>Version {playGen.version_label} · {playGen.model} · {playGen.requested_duration_s}s</span>
                            <button className="text-neutral-400 hover:text-white text-lg" onClick={() => setPlayGen(null)}>×</button>
                        </div>
                        <video src={absoluteAssetUrl(playGen.signed_url)} controls autoPlay className="w-full" />
                    </div>
                </div>
            )}

            {/* Bulk-generate confirm modal */}
            {bulkOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={() => setBulkOpen(false)}>
                    <div data-testid="bulk-modal" className="max-w-lg w-full rounded-lg border border-neutral-200 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
                            <div className="text-sm font-semibold text-neutral-900">Generate all cinematic B-roll clips</div>
                            <button className="text-neutral-400 hover:text-neutral-800" onClick={() => setBulkOpen(false)}>×</button>
                        </div>
                        <div className="p-5 space-y-4 text-sm">
                            {!bulkPreview ? (
                                <div className="text-neutral-500">Loading preview…</div>
                            ) : bulkPreview.scenes.length === 0 ? (
                                <div className="text-neutral-500">No eligible scenes — every scene that needs a cinematic clip already has a selected generation or is at cap.</div>
                            ) : (
                                <>
                                    <div className="text-[13px] text-neutral-700">
                        This will queue <span className="mono font-medium">{bulkPreview.scenes.length}</span> cinematic clip generations
                        for a total estimated cost of <span className="mono font-medium">${displayCurrency(bulkPreview.total_estimated_cost_usd)}</span>.
                                    </div>
                                    <div className="rounded-md border border-neutral-200 max-h-56 overflow-y-auto text-[12px]">
                                        {bulkPreview.scenes.map((s) => (
                                            <div key={s.scene_id} className="flex items-center justify-between border-b border-neutral-100 last:border-0 px-3 py-1.5">
                                                <span>{s.scene_number} · {s.mode}</span>
                                                <span className="mono text-neutral-500">${displayCurrency(s.estimated_cost_usd)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 flex items-start gap-2">
                                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                        <span>Trainer scenes use best-effort identity conditioning — the provider does not guarantee likeness. Reviewer must approve each generation.</span>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="border-t border-neutral-200 px-5 py-3 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                            <Button
                                onClick={confirmBulk}
                                disabled={!bulkPreview || bulkPreview.scenes.length === 0}
                                data-testid="bulk-confirm"
                                className="bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
                            >
                                Confirm & queue
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Bulk-generate voices modal */}
            {voiceBulkOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={() => setVoiceBulkOpen(false)}>
                    <div data-testid="voice-bulk-modal" className="max-w-lg w-full rounded-lg border border-neutral-200 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
                            <div className="text-sm font-semibold text-neutral-900">Generate all narrations</div>
                            <button className="text-neutral-400 hover:text-neutral-800" onClick={() => setVoiceBulkOpen(false)}>×</button>
                        </div>
                        <div className="p-5 space-y-4 text-sm">
                            {!voiceBulkPreview ? (
                                <div className="text-neutral-500">Loading preview…</div>
                            ) : voiceBulkPreview.scenes.length === 0 ? (
                                <div className="text-neutral-500">Every scene with narration already has a READY voice asset.</div>
                            ) : (
                                <>
                                    <div className="text-[13px] text-neutral-700">
                                        This will queue <span className="mono font-medium">{voiceBulkPreview.scenes.length}</span> voice generations
                                        for a total estimated cost of <span className="mono font-medium">${displayCurrency(voiceBulkPreview.total_estimated_cost_usd, 4)}</span>.
                                    </div>
                                    <div className="rounded-md border border-neutral-200 max-h-56 overflow-y-auto text-[12px]">
                                        {voiceBulkPreview.scenes.map((s) => (
                                            <div key={s.scene_id} className="flex items-center justify-between border-b border-neutral-100 last:border-0 px-3 py-1.5">
                                                <span>{s.scene_number} · {s.char_count} chars</span>
                                                <span className="mono text-neutral-500">${displayCurrency(s.estimated_cost_usd, 4)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="border-t border-neutral-200 px-5 py-3 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setVoiceBulkOpen(false)}>Cancel</Button>
                            <Button
                                onClick={confirmVoiceBulk}
                                disabled={!voiceBulkPreview || voiceBulkPreview.scenes.length === 0}
                                data-testid="voice-bulk-confirm"
                                className="bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
                            >
                                Confirm &amp; queue
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Bulk-generate motion modal */}
            {motionBulkOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={() => setMotionBulkOpen(false)}>
                    <div data-testid="motion-bulk-modal" className="max-w-lg w-full rounded-lg border border-neutral-200 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
                            <div className="text-sm font-semibold text-neutral-900">Generate all motion clips</div>
                            <button className="text-neutral-400 hover:text-neutral-800" onClick={() => setMotionBulkOpen(false)}>×</button>
                        </div>
                        <div className="p-5 space-y-4 text-sm">
                            {!motionBulkPreview ? (
                                <div className="text-neutral-500">Loading preview…</div>
                            ) : motionBulkPreview.total === 0 ? (
                                <div className="text-neutral-500">Every eligible Type D/E/F scene already has a READY motion render.</div>
                            ) : (
                                <>
                                    <div className="text-[13px] text-neutral-700">
                                        This will render <span className="mono font-medium">{motionBulkPreview.total}</span> deterministic mp4 clips (no external cost — CPU only).
                                    </div>
                                    <div className="rounded-md border border-neutral-200 max-h-56 overflow-y-auto text-[12px]">
                                        {motionBulkPreview.scenes.map((s) => (
                                            <div key={s.scene_id} className="flex items-center justify-between border-b border-neutral-100 last:border-0 px-3 py-1.5">
                                                <span>{s.scene_number}</span>
                                                <span className="mono text-neutral-500">{s.scene_type.replace("_", " ").toLowerCase()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="border-t border-neutral-200 px-5 py-3 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setMotionBulkOpen(false)}>Cancel</Button>
                            <Button
                                onClick={confirmMotionBulk}
                                disabled={!motionBulkPreview || motionBulkPreview.total === 0}
                                data-testid="motion-bulk-confirm"
                                className="bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
                            >
                                Confirm &amp; render
                            </Button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

// -------------------------- sub-components --------------------------

function SceneStatus({ scene }) {
    const ok = (scene.approval_state || "").toUpperCase() === "APPROVED";
    const Icon = ok ? CheckCircle2 : AlertCircle;
    return (
        <span data-testid={`scene-status-${scene.scene_number}`} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
            <Icon size={10} /> {ok ? "Approved" : "Needs review"}
        </span>
    );
}

function SceneRow({ scene, selected, onSelect, onDragStart, onDragOver, onDrop, voiceOutOfDate, moduleId, onChanged }) {
    const custom = !!scene.custom_prompt || !!scene.voice_override_voice_id;
    return (
        <div
            data-testid={`scene-row-${scene.scene_number}`}
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onClick={onSelect}
            className={`group cursor-pointer rounded-md border px-2.5 py-2 text-xs transition-colors ${
                selected ? "border-neutral-900 bg-neutral-50" : "border-transparent hover:bg-neutral-50"
            }`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="mono text-[10px] font-medium text-neutral-500 w-8 shrink-0">{scene.scene_number}</span>
                    <span className="truncate text-neutral-800">{SCENE_TYPE_LABEL[scene.scene_type] || scene.scene_type}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {voiceOutOfDate && (
                        <span data-testid={`scene-voice-stale-${scene.scene_number}`} className="inline-flex rounded px-1 py-0.5 text-[9px] font-medium bg-amber-50 text-amber-800" title="Voice out of date">V!</span>
                    )}
                    <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-medium ${RISK_CLASS[scene.fidelity_risk] || RISK_CLASS.LOW}`}>{RISK_LABEL[scene.fidelity_risk] || RISK_LABEL.LOW}</span>
                    <SceneMenu scene={scene} moduleId={moduleId} onChanged={onChanged} />
                </div>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
                <SceneStatus scene={scene} />
                {custom && <span data-testid={`scene-custom-tag-${scene.scene_number}`} className="inline-flex rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700" title={scene.custom_prompt || "Custom voice"}>Custom</span>}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[10px] text-neutral-500">
                <Clock size={9} /> {scene.duration_seconds}s
                <span>·</span>
                <span>{PRESENCE_LABEL[scene.trainer_presence] || scene.trainer_presence}</span>
                {scene.runway_required && <span className="text-purple-700">·  Cinematic clip</span>}
            </div>
        </div>
    );
}

function ScenePreview({ scene, media, selectedGeneration }) {
    const linked = media.find((m) => m.id === scene.source_media_asset_id);
    const showSelected = selectedGeneration && selectedGeneration.status === "COMPLETED" && selectedGeneration.signed_url;
    return (
        <div className="space-y-4">
            <div className="relative aspect-video rounded-lg overflow-hidden border border-neutral-200 bg-neutral-900">
                {showSelected ? (
                    <video src={absoluteAssetUrl(selectedGeneration.signed_url)} className="h-full w-full object-cover" controls preload="metadata" data-testid="selected-generation-video" />
                ) : linked?.signed_url ? (
                    <img src={absoluteAssetUrl(linked.signed_url)} alt="" className="h-full w-full object-cover opacity-90" />
                ) : scene.runway_required ? (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-neutral-400 text-sm">
                        <Film size={22} />
                        <div>A cinematic clip will be generated for this scene</div>
                    </div>
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-neutral-400 text-sm">
                        <Camera size={22} />
                    </div>
                )}
                {!showSelected && scene.text_overlay?.length > 0 && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 space-y-1">
                        {scene.text_overlay.map((t, i) => (
                            <div key={i} data-testid={`overlay-${i}`} className="text-white text-sm font-medium tracking-tight">{t.text}</div>
                        ))}
                    </div>
                )}
            </div>
            {scene.runway_required && (
                <details className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs">
                    <summary className="cursor-pointer font-medium text-neutral-700">
                        Composed cinematic prompt (mode: {scene.runway_mode || "—"})
                    </summary>
                    <pre data-testid="composed-runway-prompt" className="mt-2 whitespace-pre-wrap font-sans text-neutral-600 leading-relaxed">{scene.runway_prompt_text || "—"}</pre>
                </details>
            )}
            {scene.trainer_dialogue && (
                <div className="rounded-md border-l-4 border-neutral-900 bg-neutral-50 pl-3 py-2 text-sm">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Trainer says</div>
                    <div className="text-neutral-800">{scene.trainer_dialogue}</div>
                </div>
            )}
            {scene.voiceover && scene.voiceover !== scene.trainer_dialogue && (
                <div className="rounded-md border-l-4 border-neutral-400 bg-neutral-50 pl-3 py-2 text-sm">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Voiceover</div>
                    <div className="text-neutral-800">{scene.voiceover}</div>
                </div>
            )}
            <div className="rounded-md border border-dashed border-neutral-200 bg-neutral-50 p-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">
                    <Mic2 size={11} />
                    <span>Narration audio</span>
                </div>
                <div className="mt-1 text-xs text-neutral-500 italic">
                    Narration is generated from the voiceover text.
                </div>
            </div>
        </div>
    );
}


function ScenePropertiesHeader({
    scene, canEdit, onApprove, onUnapprove,
    onRegenerateVoice, onRegenerateVisual, onRegenerateAll, busy, onPatch,
}) {
    const state = scene.approval_state || "DRAFT";
    const DEFAULT_STATE_LABEL = { label: "In progress", class: "bg-neutral-100 text-neutral-700 border-neutral-200" };
    const stateLabel = ({
        APPROVED: "Approved",
        NEEDS_REAPPROVAL: "Needs re-approval",
        DRAFT: "Draft",
        GENERATING: "Generating",
        FAILED: "Failed",
        PENDING: "Pending",
        UNDER_REVIEW: "Under review",
    })[state] || DEFAULT_STATE_LABEL.label;
    const stateClass = ({
        APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
        NEEDS_REAPPROVAL: "bg-amber-100 text-amber-900 border-amber-300",
        DRAFT: "bg-neutral-100 text-neutral-700 border-neutral-200",
        GENERATING: "bg-blue-100 text-blue-800 border-blue-200",
        FAILED: "bg-red-100 text-red-800 border-red-200",
        PENDING: "bg-neutral-100 text-neutral-700 border-neutral-200",
        UNDER_REVIEW: "bg-blue-50 text-blue-700 border-blue-200",
    })[state] || DEFAULT_STATE_LABEL.class;

    const previewUrl = scene.selected_motion_render_id
        ? `/api/motion-renders/${scene.selected_motion_render_id}/stream`
        : null;

    return (
        <div className="space-y-3">
            {/* State chip + review subchips */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                        <span
                            data-testid="scene-state-chip"
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${stateClass}`}
                        >
                            {stateLabel}
                        </span>
                    </div>
                    <p
                        data-testid="scene-state-help"
                        className="text-[12px] leading-[1.4] text-neutral-500"
                    >
                        {STATE_HELP[state] || DEFAULT_STATE_HELP}
                    </p>
                </div>
                <SceneReviewChips scene={scene} onPatch={onPatch} />
            </div>

            {/* Action buttons */}
            {canEdit && (
                <div className="space-y-3" data-testid="scene-action-buttons">
                    <div className="flex flex-wrap gap-2">
                        <div className="flex flex-col gap-1">
                            {state !== "APPROVED" ? (
                                <>
                                    <Button
                                        size="sm"
                                        onClick={() => onApprove(scene.id)}
                                        disabled={busy}
                                        data-testid="scene-approve-btn"
                                        className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                                    >
                                        <Check size={12} className="mr-1" />
                                        Approve Scene
                                    </Button>
                                    <p className="text-[12px] leading-[1.4] text-neutral-500 max-w-[320px]">
                                        {MICROCOPY.approve}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onUnapprove(scene.id)}
                                        disabled={busy}
                                        data-testid="scene-unapprove-btn"
                                        className="h-7 text-[11px]"
                                    >
                                        Withdraw approval
                                    </Button>
                                    <p className="text-[12px] leading-[1.4] text-neutral-500 max-w-[320px]">
                                        {MICROCOPY.unapprove}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="h-px bg-neutral-200" />
                    <div className="flex flex-wrap gap-x-4 gap-y-3">
                        <div className="flex flex-col gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRegenerateVoice(scene.id)}
                                disabled={busy}
                                data-testid="scene-regen-voice-btn"
                                className="h-7 text-[11px]"
                            >
                                <Mic2 size={12} className="mr-1" />
                                Regenerate voiceover
                            </Button>
                            <p className="text-[12px] leading-[1.4] text-neutral-500 max-w-[280px]">
                                {MICROCOPY.regen_voice}
                            </p>
                        </div>
                        <div className="flex flex-col gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRegenerateVisual(scene.id)}
                                disabled={busy}
                                data-testid="scene-regen-visual-btn"
                                className="h-7 text-[11px]"
                            >
                                <Camera size={12} className="mr-1" />
                                Regenerate visual
                            </Button>
                            <p className="text-[12px] leading-[1.4] text-neutral-500 max-w-[280px]">
                                {MICROCOPY.regen_visual}
                            </p>
                        </div>
                        <div className="flex flex-col gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRegenerateAll(scene.id)}
                                disabled={busy}
                                data-testid="scene-regen-all-btn"
                                className="h-7 text-[11px]"
                            >
                                <RefreshCw size={12} className={`mr-1 ${busy ? "animate-spin" : ""}`} />
                                Regenerate entire scene
                            </Button>
                            <p className="text-[12px] leading-[1.4] text-neutral-500 max-w-[280px]">
                                {MICROCOPY.regen_all}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Inline MP4 preview */}
            {previewUrl && (
                <div data-testid="scene-preview-wrap" className="rounded border border-neutral-200 overflow-hidden bg-black">
                    <video
                        key={previewUrl}
                        src={absoluteAssetUrl(previewUrl)}
                        controls
                        preload="metadata"
                        className="w-full h-auto max-h-64 bg-black"
                        data-testid="scene-preview-video"
                    />
                </div>
            )}
        </div>
    );
}


// Phase 5.2 Task 5 — onboarding helper + microcopy constants (plain
// English, white-labelled, no vendor names).
const MICROCOPY = {
    approve: "Locks the script, images, and timing. The final video will match this exactly.",
    unapprove: "Unlocks the scene so you can edit again. The final video will be rebuilt after re-approval.",
    regen_voice: "Rebuilds the narration with the latest script. Takes about 15 seconds.",
    regen_visual: "Rebuilds the on-screen visuals. Use this after changing images or layout.",
    regen_all: "Rebuilds narration AND visuals together. Takes about a minute.",
    preview: "Watch this scene the way it will appear in the final video.",
};

const STATE_HELP = {
    APPROVED: "Ready to ship in the final video.",
    NEEDS_REAPPROVAL: "You edited this scene. Approve again to include your changes in the final video.",
    DRAFT: "Review and approve to include this scene.",
    GENERATING: "Rebuilding — this takes about a minute.",
    FAILED: "Something went wrong. Click Regenerate to try again.",
    PENDING: "Queued — the system will pick this up shortly.",
    UNDER_REVIEW: "A reviewer is taking a look at this scene.",
};

const DEFAULT_STATE_HELP = "Review and approve to include this scene.";

// P0 hotfix — error boundary around the Properties panel so a bad
// scene payload shows a friendly "Something went wrong" fallback
// instead of blanking the whole app. The boundary logs the error +
// component stack and offers a one-click reload.
class PropertiesErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorText: "" };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, errorText: String(error?.message || error || "unknown") };
    }
    componentDidCatch(error, info) {
        // Surfaces in the browser console for Playwright / devtools.
        // eslint-disable-next-line no-console
        console.error("[PropertiesErrorBoundary]", error, info?.componentStack);
    }
    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div
                data-testid="scene-properties-error"
                className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900 space-y-2"
                role="alert"
            >
                <div className="font-semibold">Something went wrong opening this scene.</div>
                <div className="text-[12px] text-red-800 leading-relaxed">
                    Reload the page to try again — your approvals are safe.
                </div>
                <button
                    type="button"
                    data-testid="scene-properties-error-reload"
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center rounded-md border border-red-300 bg-white px-2.5 py-1 text-[11px] font-medium text-red-900 hover:bg-red-100"
                >
                    Reload the page
                </button>
            </div>
        );
    }
}

function OnboardingHelper() {
    const KEY = "sb-editor-onboarding-dismissed-v2";
    const [dismissed, setDismissed] = React.useState(() => {
        try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
    });
    const [open, setOpen] = React.useState(!dismissed);
    if (dismissed) return null;
    return (
        <div
            data-testid="sb-onboarding-helper"
            className="px-4 py-3 flex items-start gap-3 border-b border-neutral-200 bg-indigo-50/50"
        >
            <div className="flex-1">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex items-center gap-2 text-sm font-semibold text-indigo-900"
                    data-testid="sb-onboarding-toggle"
                >
                    <ChevronDown size={14} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
                    How this page works
                </button>
                {open && (
                    <ul className="mt-2 space-y-1 text-[13px] leading-[1.45] text-indigo-900/90">
                        <li>• Edit the script, images, or layout for any scene using the controls below.</li>
                        <li>• Approve each scene when you're happy with it. The final video rebuilds automatically after your last approval.</li>
                        <li>• If something looks off, click Regenerate — no coding required.</li>
                    </ul>
                )}
            </div>
            <button
                type="button"
                onClick={() => {
                    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
                    setDismissed(true);
                }}
                className="shrink-0 text-[11px] text-indigo-700 hover:text-indigo-900 font-medium"
                data-testid="sb-onboarding-dismiss"
            >
                Got it
            </button>
        </div>
    );
}


function SceneReviewChips({ scene, onPatch }) {
    return (
        <div className="flex items-center gap-1.5">
            <Button size="sm" variant={scene.review_state === "ACCEPTED" ? "default" : "outline"} className={`h-6 text-[10px] ${scene.review_state === "ACCEPTED" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`} onClick={() => onPatch(scene.id, { review_state: "ACCEPTED" })} data-testid="scene-accept">
                <Check size={11} className="mr-1" /> Accept
            </Button>
            <Button size="sm" variant={scene.review_state === "EDITED" ? "default" : "outline"} className="h-6 text-[10px]" onClick={() => onPatch(scene.id, { review_state: "EDITED" })} data-testid="scene-edit">
                <Pencil size={11} className="mr-1" /> Edited
            </Button>
            <Button size="sm" variant={scene.review_state === "FLAGGED" ? "default" : "outline"} className={`h-6 text-[10px] ${scene.review_state === "FLAGGED" ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-600"}`} onClick={() => onPatch(scene.id, { review_state: "FLAGGED" })} data-testid="scene-flag">
                <Flag size={11} className="mr-1" /> Flag
            </Button>
        </div>
    );
}

function ScenePropertiesTabs({
    scene, media, steps, onPatch, orgSettings, genForm, setGenForm,
    voiceAssetsForScene, availableVoices, onGenerateVoice, onSelectVoice,
    onRegenerateVoice, voiceJobActive, outOfDate,
}) {
    return (
        <Tabs defaultValue="content">
            <TabsList data-testid="prop-tabs" className="bg-transparent border-b border-neutral-200 rounded-none p-0 h-auto flex w-full">
                {[
                    { v: "content", label: "content" },
                    { v: "visual", label: "visual" },
                    { v: "runway", label: "cinematic" },
                    { v: "timing", label: "timing" },
                    { v: "fidelity", label: "fidelity" },
                ].map((t) => (
                    <TabsTrigger
                        key={t.v} value={t.v} data-testid={`prop-tab-${t.v}`}
                        className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-2 py-2 text-[11px] capitalize"
                    >
                        {t.v === "timing" ? "Timing & Audio" : t.label}
                    </TabsTrigger>
                ))}
            </TabsList>
            <TabsContent value="content" className="pt-3">
                <ContentTab scene={scene} onPatch={onPatch} />
            </TabsContent>
            <TabsContent value="visual" className="pt-3">
                <VisualTab scene={scene} media={media} steps={steps} onPatch={onPatch} />
            </TabsContent>
            <TabsContent value="runway" className="pt-3">
                <RunwayTab scene={scene} onPatch={onPatch} orgSettings={orgSettings} genForm={genForm} setGenForm={setGenForm} />
            </TabsContent>
                            <TabsContent value="timing" className="pt-3">
                                <TimingTab
                                    scene={scene}
                                    onPatch={onPatch}
                                    voiceAssets={voiceAssetsForScene}
                                    availableVoices={availableVoices}
                                    onGenerateVoice={onGenerateVoice}
                                    onSelectVoice={onSelectVoice}
                                    onRegenerateVoice={onRegenerateVoice}
                                    voiceJobActive={voiceJobActive}
                                    outOfDate={outOfDate}
                                />
                            </TabsContent>
            <TabsContent value="fidelity" className="pt-3">
                <FidelityTab scene={scene} />
            </TabsContent>
        </Tabs>
    );
}

function EditableTextarea({ value, onSave, rows = 3, ...rest }) {
    const [v, setV] = useState(value || "");
    useEffect(() => { setV(value || ""); }, [value]);
    return (
        <Textarea
            rows={rows}
            value={v}
            onChange={(e) => setV(e.target.value)}
            onBlur={() => { if (v !== (value || "")) onSave(v); }}
            {...rest}
        />
    );
}

function ContentTab({ scene, onPatch }) {
    const [sameAs, setSameAs] = useState(
        (scene.voiceover || "") === (scene.trainer_dialogue || "")
    );
    return (
        <div className="space-y-3 text-xs">
            <div>
                <Label className="text-xs mb-1 block">Trainer dialogue</Label>
                <EditableTextarea
                    value={scene.trainer_dialogue}
                    onSave={(v) => {
                        const patch = { trainer_dialogue: v };
                        if (sameAs) patch.voiceover = v;
                        onPatch(scene.id, patch);
                    }}
                    data-testid="prop-trainer-dialogue"
                    rows={3}
                />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-neutral-700">
                <input type="checkbox" checked={sameAs} onChange={(e) => setSameAs(e.target.checked)} data-testid="voiceover-same-as" />
                Voiceover same as trainer dialogue
            </label>
            {!sameAs && (
                <div>
                    <Label className="text-xs mb-1 block">Voiceover</Label>
                    <EditableTextarea
                        value={scene.voiceover}
                        onSave={(v) => onPatch(scene.id, { voiceover: v })}
                        data-testid="prop-voiceover"
                        rows={3}
                    />
                </div>
            )}
            <div>
                <Label className="text-xs mb-1 block">Visual description</Label>
                <EditableTextarea
                    value={scene.visual_description}
                    onSave={(v) => onPatch(scene.id, { visual_description: v })}
                    data-testid="prop-visual-desc"
                    rows={2}
                />
            </div>
            <TextOverlayEditor scene={scene} onPatch={onPatch} />
        </div>
    );
}

function TextOverlayEditor({ scene, onPatch }) {
    const [rows, setRows] = useState(scene.text_overlay || []);
    useEffect(() => { setRows(scene.text_overlay || []); }, [scene.id, scene.text_overlay]);
    const commit = (next) => onPatch(scene.id, { text_overlay: next });
    return (
        <div className="space-y-2">
            <Label className="text-xs">Text overlay</Label>
            {rows.length === 0 && <div className="text-[11px] text-neutral-400">None.</div>}
            {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                    <Input
                        value={r.text || ""}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                        onBlur={() => commit(rows)}
                        placeholder="Overlay text"
                        data-testid={`prop-overlay-text-${i}`}
                    />
                    <Input
                        value={r.timing || ""}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, timing: e.target.value } : x)))}
                        onBlur={() => commit(rows)}
                        placeholder="0-3s" className="w-20"
                        data-testid={`prop-overlay-timing-${i}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => { const next = rows.filter((_, j) => j !== i); setRows(next); commit(next); }} className="text-red-600">×</Button>
                </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setRows([...rows, { text: "", timing: "" }])} data-testid="prop-overlay-add">+ Add overlay</Button>
        </div>
    );
}

function VisualTab({ scene, media, steps, onPatch }) {
    return (
        <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Scene type</Label>
                    <Select value={scene.scene_type || ""} onValueChange={(v) => onPatch(scene.id, { scene_type: v })}>
                        <SelectTrigger data-testid="prop-scene-type"><SelectValue placeholder="— pick a scene type —" /></SelectTrigger>
                        <SelectContent>
                            {Object.entries(SCENE_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs">Trainer presence</Label>
                    <Select value={scene.trainer_presence || ""} onValueChange={(v) => onPatch(scene.id, { trainer_presence: v })}>
                        <SelectTrigger data-testid="prop-trainer-presence"><SelectValue placeholder="— pick a trainer presence —" /></SelectTrigger>
                        <SelectContent>
                            {Object.entries(PRESENCE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label className="text-xs">Source image</Label>
                <Select value={scene.source_media_asset_id || ""} onValueChange={(v) => onPatch(scene.id, { source_media_asset_id: v })}>
                    <SelectTrigger data-testid="prop-source-media"><SelectValue placeholder="— none —" /></SelectTrigger>
                    <SelectContent>
                        {media.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                                #{m.order_index_in_doc}{m.step_number ? ` · step ${m.step_number}` : ""}{m.classification ? ` · ${m.classification}` : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="text-xs">Source step</Label>
                <Select value={scene.source_step_id || ""} onValueChange={(v) => onPatch(scene.id, { source_step_id: v })}>
                    <SelectTrigger data-testid="prop-source-step"><SelectValue placeholder="— none —" /></SelectTrigger>
                    <SelectContent>
                        {steps.map((s) => <SelectItem key={s.id} value={s.id}>Step {s.step_number}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Camera</Label>
                    <EditableTextarea value={scene.camera_direction} rows={2} onSave={(v) => onPatch(scene.id, { camera_direction: v })} data-testid="prop-camera" />
                </div>
                <div>
                    <Label className="text-xs">Animation</Label>
                    <EditableTextarea value={scene.animation_direction} rows={2} onSave={(v) => onPatch(scene.id, { animation_direction: v })} data-testid="prop-animation" />
                </div>
            </div>
            <div>
                <Label className="text-xs">Transition</Label>
                <Select value={scene.transition} onValueChange={(v) => onPatch(scene.id, { transition: v })}>
                    <SelectTrigger data-testid="prop-transition"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {["CUT", "DISSOLVE", "FADE"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}

function RunwayTab({ scene, onPatch, orgSettings, genForm, setGenForm }) {
    const rp = scene.runway_prompt_structured || {};
    const fields = [
        "subject", "environment", "action", "camera", "lens_composition",
        "lighting", "movement", "mood", "realism", "corporate_style", "restrictions",
    ];
    const models = orgSettings?.available_models || [];
    const currentModel = models.find((m) => m.id === (genForm?.model || orgSettings?.settings?.default_premium_model_id));
    const isTrainerId = scene.runway_mode === "TRAINER_IDENTITY";
    return (
        <div className="space-y-3 text-xs">
            <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={!!scene.runway_required} onChange={(e) => onPatch(scene.id, { runway_required: e.target.checked })} data-testid="prop-runway-required" />
                    Cinematic clip required
                </label>
                <div className="flex-1">
                    <Select value={scene.runway_mode || ""} onValueChange={(v) => onPatch(scene.id, { runway_mode: v })}>
                        <SelectTrigger data-testid="prop-runway-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
                        <SelectContent>
                            {["TEXT_TO_VIDEO", "IMAGE_TO_VIDEO_REFERENCE", "TRAINER_IDENTITY"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {isTrainerId && (
                <div data-testid="trainer-identity-caveat" className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 flex items-start gap-2">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    <span>Identity preservation is best-effort — reviewer must approve each generation. The provider does not contractually guarantee likeness.</span>
                </div>
            )}
            {orgSettings && false && (
                <div className="grid grid-cols-3 gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2" data-testid="hidden-runway-defaults-knobs">
                    <div className="col-span-3 text-[10px] uppercase tracking-widest text-neutral-500">Generation defaults</div>
                    <div>
                        <Label className="text-[10px]">Model</Label>
                        <Select value={genForm?.model || ""} onValueChange={(v) => setGenForm({ ...genForm, model: v })}>
                            <SelectTrigger data-testid="runway-model-select" className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · ${displayCurrency(m.price_per_second_usd, 4)}/s</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px]">Duration</Label>
                        <Select value={String(genForm?.duration_s || 6)} onValueChange={(v) => setGenForm({ ...genForm, duration_s: parseInt(v, 10) })}>
                            <SelectTrigger data-testid="runway-duration-select" className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {(currentModel?.supported_durations || [5, 6, 7, 8, 10]).map((d) => <SelectItem key={d} value={String(d)}>{d}s</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px]">Aspect</Label>
                        <Select value={genForm?.aspect_ratio || "1280:720"} onValueChange={(v) => setGenForm({ ...genForm, aspect_ratio: v })}>
                            <SelectTrigger data-testid="runway-aspect-select" className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {(currentModel?.aspect_ratios || ["1280:720"]).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-3 text-[10px] text-neutral-500 mono">
                        est. cost: ${displayCurrency((currentModel?.price_per_second_usd || 0.12) * (genForm?.duration_s || 6))} per clip
                    </div>
                </div>
            )}
            {fields.map((f) => (
                <div key={f}>
                    <Label className="text-[10px] uppercase tracking-widest text-neutral-500">{f.replaceAll("_", " ")}</Label>
                    <EditableTextarea
                        value={rp[f]}
                        rows={2}
                        onSave={(v) => onPatch(scene.id, { runway_prompt_structured: { ...rp, [f]: v } })}
                        data-testid={`prop-rp-${f}`}
                    />
                </div>
            ))}
            <div>
                <Label className="text-xs">Composed prompt</Label>
                <pre className="mt-1 rounded-md border border-neutral-200 bg-neutral-50 p-2 whitespace-pre-wrap font-sans text-[11px] text-neutral-600 leading-relaxed" data-testid="prop-runway-text">
                    {scene.runway_prompt_text || "—"}
                </pre>
            </div>
        </div>
    );
}

// ---- Generations panel ----

const GEN_STATUS_CLASS = {
    QUEUED: "bg-neutral-100 text-neutral-700",
    PROCESSING: "bg-blue-50 text-blue-700",
    COMPLETED: "bg-emerald-50 text-emerald-700",
    FAILED: "bg-red-50 text-red-700",
    CANCELLED: "bg-neutral-100 text-neutral-500",
};

function GenerationsPanel({ scene, generations, onStart, onSelect, onCancel, onPlay, settings }) {
    const [locked, setLocked] = useState(null);
    // Detect technical lock: source_media_asset has classification=TECHNICAL_LOCKED.
    // (Server enforces too; UI hint only.)
    if (!scene.runway_required) return null;
    const cap = settings?.max_generations_per_scene || 3;
    const nonCancelled = generations.filter((g) => g.status !== "CANCELLED");
    const atCap = nonCancelled.length >= cap;
    return (
        <div data-testid="generations-panel" className="mt-4 rounded-lg border border-neutral-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
                    Cinematic clip generations · {scene.runway_mode || "TEXT_TO_VIDEO"}
                </div>
                <Button
                    size="sm" variant="outline"
                    onClick={() => onStart({})}
                    disabled={atCap}
                    title={atCap ? `Per-scene cap of ${cap} reached — cancel an existing version first` : ""}
                    data-testid="new-generation-btn"
                >
                    <Wand2 size={12} className="mr-1.5" /> Generate
                </Button>
            </div>
            {generations.length === 0 ? (
                <div className="text-[12px] text-neutral-500 italic">No generations yet. Click "Generate" to create one — generation costs apply per clip.</div>
            ) : (
                <div className="grid grid-cols-3 gap-3">
                    {generations.map((g) => (
                        <GenerationTile
                            key={g.id}
                            gen={g}
                            selected={scene.selected_generation_id === g.id}
                            onPlay={() => onPlay(g)}
                            onSelect={() => onSelect(g.id)}
                            onCancel={() => onCancel(g.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
function MotionRenderPanel({ scene, renders, onRender, onSelect, active }) {
    const eligible = ["D_TECHNICAL_LOCKED", "E_MOTION_GRAPHICS", "F_SPLIT_TRAINER_TECHNICAL"].includes(scene.scene_type);
    if (!eligible) return null;
    const selected = renders.find((r) => r.is_selected);
    return (
        <div data-testid="motion-render-panel" className="mt-4 rounded-lg border border-neutral-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
                    <Film size={12} strokeWidth={1.75} /> Motion clip · {scene.scene_type.replace(/_/g, " ").toLowerCase()}
                </div>
                <Button
                    size="sm" variant="outline"
                    onClick={() => onRender(scene.id)}
                    disabled={active}
                    data-testid="motion-render-btn"
                >
                    <RefreshCw size={12} className={active ? "animate-spin mr-1.5" : "mr-1.5"} />
                    {active ? "Rendering…" : "Render motion"}
                </Button>
            </div>
            {selected && selected.signed_url ? (
                <div className="rounded-md bg-neutral-900 overflow-hidden">
                    <video src={absoluteAssetUrl(selected.signed_url)} controls className="w-full aspect-video" preload="metadata" data-testid="motion-preview-video" />
                    <div className="px-2 py-1 mono text-[10px] text-neutral-400 flex items-center justify-between">
                        <span>{selected.template_name}{selected.fallback_used ? " · fallback" : ""} · {selected.duration_s.toFixed(1)}s</span>
                        <span>wall {selected.render_wall_time_ms}ms</span>
                    </div>
                </div>
            ) : (
                <div className="text-[12px] text-neutral-500 italic">No motion render yet. Click "Render motion" to generate a deterministic mp4.</div>
            )}
            {renders.length > 1 && (
                <div className="mt-3">
                    <div className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 mb-1.5">History ({renders.length})</div>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                        {renders.map((r) => (
                            <div key={r.id} data-testid={`motion-history-${r.id.slice(0, 8)}`} className={`rounded-md border p-2 flex items-center gap-2 ${r.is_selected ? "border-neutral-900 bg-neutral-50" : "border-neutral-200"}`}>
                                <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-medium ${r.status === "READY" ? "bg-emerald-50 text-emerald-700" : r.status === "FAILED" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>{r.status}</span>
                                <span className="mono text-[10px] text-neutral-500 flex-1 truncate">{r.template_name}{r.fallback_used ? " · fallback" : ""} · {r.duration_s.toFixed(1)}s</span>
                                {r.status === "READY" && !r.is_selected && (
                                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onSelect(r.id, scene.id)}>Use this</Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}



function GenerationTile({ gen, selected, onPlay, onSelect, onCancel }) {
    const statusClass = GEN_STATUS_CLASS[gen.status] || GEN_STATUS_CLASS.QUEUED;
    return (
        <div
            data-testid={`gen-tile-${gen.version_label}`}
            className={`rounded-md border p-2 transition-colors ${selected ? "border-neutral-900 bg-neutral-50 ring-1 ring-neutral-900" : "border-neutral-200 bg-white"}`}
        >
            <div className="flex items-center justify-between mb-1">
                <div className="mono text-[11px] font-medium">Version {gen.version_label}</div>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-medium ${statusClass}`} data-testid={`gen-status-${gen.version_label}`}>
                    {gen.status}
                </span>
            </div>
            <div className="aspect-video rounded overflow-hidden bg-neutral-900">
                {gen.status === "COMPLETED" && gen.signed_url ? (
                    <video src={absoluteAssetUrl(gen.signed_url)} className="h-full w-full object-cover" muted preload="metadata" />
                ) : gen.status === "FAILED" ? (
                    <div className="flex h-full w-full items-center justify-center text-red-300 text-[10px] p-2 text-center">
                        {gen.error_code || "FAILED"}
                    </div>
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-neutral-400 text-[10px] gap-1">
                        <RefreshCw size={11} className="animate-spin" /> {gen.status.toLowerCase()}
                    </div>
                )}
            </div>
            <div className="mt-1 mono text-[10px] text-neutral-500">
                {gen.requested_duration_s}s · ${displayCurrency(gen.estimated_cost_usd || 0)}
                {gen.model && <span className="ml-1">· {gen.model}</span>}
            </div>
            {gen.error_message && (
                <div className="mt-1 text-[10px] text-red-600 line-clamp-2" title={gen.error_message}>{gen.error_message}</div>
            )}
            {gen.status === "FAILED" && gen.error_code === "MODERATION_OUTPUT" && (
                <div data-testid={`moderation-tip-${gen.version_label}`} className="mt-1 text-[10px] text-amber-700">
                    The provider rejected the generated frames. Try a different reference image (a real photograph works best).
                </div>
            )}
            <div className="mt-2 flex items-center gap-1">
                {gen.status === "COMPLETED" && gen.signed_url && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] flex-1" onClick={onPlay} data-testid={`gen-play-${gen.version_label}`}>Play</Button>
                )}
                {gen.status === "COMPLETED" && !selected && (
                    <Button size="sm" className="h-6 text-[10px] bg-neutral-900 text-white hover:bg-neutral-800 flex-1" onClick={onSelect} data-testid={`gen-use-${gen.version_label}`}>Use this</Button>
                )}
                {selected && <span className="text-[10px] text-emerald-700 flex-1 text-center">Selected</span>}
                {["QUEUED", "PROCESSING"].includes(gen.status) && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-600" onClick={onCancel}>Cancel</Button>
                )}
            </div>
        </div>
    );
}

function TimingTab({
    scene, onPatch, voiceAssets = [], availableVoices = [],
    onGenerateVoice, onSelectVoice, onRegenerateVoice, voiceJobActive, outOfDate,
}) {
    const selectedVoice = voiceAssets.find((v) => v.id === scene.selected_voice_asset_id);
    const hasNarrationText = !!(scene.voiceover || scene.trainer_dialogue);
    const overrideVoiceId = scene.voice_override_voice_id || "";
    const overrideSpeed = (scene.voice_override_options && scene.voice_override_options.speed) ?? 1.0;

    const setOverride = (voice_id) => {
        if (voice_id === "__trainer_default__") {
            onPatch(scene.id, {
                voice_override_voice_id: null,
                voice_override_options: null,
            });
        } else {
            const opts = scene.voice_override_options || { speed: 1.0 };
            onPatch(scene.id, {
                voice_override_voice_id: voice_id,
                voice_override_options: { ...opts, speed: overrideSpeed },
            });
        }
    };
    const setSpeed = (speed) => {
        onPatch(scene.id, {
            voice_override_voice_id: overrideVoiceId || null,
            voice_override_options: { speed },
        });
    };

    return (
        <div className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Duration (seconds)</Label>
                    <Input
                        type="number" min={3} max={30} defaultValue={scene.duration_seconds}
                        onBlur={(e) => {
                            const v = Math.max(3, Math.min(30, parseInt(e.target.value || 8, 10)));
                            if (v !== scene.duration_seconds) onPatch(scene.id, { duration_seconds: v });
                        }}
                        data-testid="prop-duration"
                    />
                </div>
                <div>
                    <Label className="text-xs">Music intensity</Label>
                    <Select value={scene.music_intensity} onValueChange={(v) => onPatch(scene.id, { music_intensity: v })}>
                        <SelectTrigger data-testid="prop-music"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["NONE", "AMBIENT", "LIGHT", "MODERATE"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Voice section */}
            <div className="rounded-lg border border-neutral-200 bg-white p-3 space-y-3" data-testid="scene-voice-section">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
                        <Volume2 size={12} strokeWidth={1.75} /> Narration voice
                    </div>
                    {outOfDate && (
                        <span data-testid="voice-out-of-date-chip" className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                            voice out of date
                        </span>
                    )}
                </div>

                {!hasNarrationText ? (
                    <div className="text-[11px] text-neutral-400 italic">Scene has no narration text yet.</div>
                ) : (
                    <>
                        {/* Player for currently selected */}
                        {selectedVoice?.signed_url ? (
                            <div className="rounded-md bg-neutral-50 p-2">
                                <audio
                                    data-testid="scene-voice-player"
                                    controls
                                    src={absoluteAssetUrl(selectedVoice.signed_url)}
                                    className="w-full"
                                />
                                <div className="mt-1 mono text-[10px] text-neutral-500 flex items-center justify-between">
                                    <span>{selectedVoice.voice_id} · {(selectedVoice.actual_duration_s || selectedVoice.duration_estimate_s || 0).toFixed(2)}s</span>
                                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onRegenerateVoice(selectedVoice.id, scene.id)} disabled={voiceJobActive} data-testid="voice-regenerate-btn">
                                        <RefreshCw size={11} className={voiceJobActive ? "animate-spin mr-1" : "mr-1"} /> Regenerate
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button
                                size="sm" onClick={() => onGenerateVoice(scene.id)} disabled={voiceJobActive}
                                data-testid="voice-generate-btn"
                                className="bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
                            >
                                <Volume2 size={12} className="mr-1.5" />
                                {voiceJobActive ? "Generating…" : "Generate voice"}
                            </Button>
                        )}

                        {/* Voice override / speed knobs hidden per Step A — platform picks silently. */}
                        <div className="hidden" data-testid="voice-override-knobs-hidden">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-xs">Voice override</Label>
                                    <Select value={overrideVoiceId || "__trainer_default__"} onValueChange={setOverride}>
                                        <SelectTrigger data-testid="voice-override-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__trainer_default__">Use trainer default</SelectItem>
                                            {availableVoices.map((v) => (
                                                <SelectItem key={v.id} value={v.id}>{v.name} · {v.gender}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs">Speed ({overrideSpeed.toFixed(2)}×)</Label>
                                    <input
                                        type="range" min="0.5" max="1.5" step="0.05"
                                        defaultValue={overrideSpeed}
                                        onMouseUp={(e) => setSpeed(parseFloat(e.target.value))}
                                        onTouchEnd={(e) => setSpeed(parseFloat(e.target.value))}
                                        className="w-full mt-2"
                                        data-testid="voice-speed-slider"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* History */}
                        {voiceAssets.length > 0 && (
                            <div>
                                <div className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 mb-1.5">History ({voiceAssets.length})</div>
                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                    {voiceAssets.map((v) => (
                                        <div key={v.id} data-testid={`voice-history-${v.id.slice(0, 8)}`} className={`rounded-md border p-2 flex items-center gap-2 ${v.is_selected ? "border-neutral-900 bg-neutral-50" : "border-neutral-200"}`}>
                                            <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-medium ${v.status === "READY" ? "bg-emerald-50 text-emerald-700" : v.status === "FAILED" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>{v.status}</span>
                                            <span className="mono text-[10px] text-neutral-500 flex-1 truncate">{v.voice_id} · {(v.actual_duration_s || v.duration_estimate_s || 0).toFixed(1)}s · ${displayCurrency(v.cost_estimate_usd || 0, 4)}</span>
                                            {v.signed_url && v.status === "READY" && (
                                                <audio src={absoluteAssetUrl(v.signed_url)} controls className="h-6 w-32" preload="none" />
                                            )}
                                            {!v.is_selected && v.status === "READY" && (
                                                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onSelectVoice(v.id, scene.id)} data-testid={`voice-select-${v.id.slice(0, 8)}`}>Use this</Button>
                                            )}
                                            {!v.is_selected && (
                                                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onRegenerateVoice(v.id, scene.id)} disabled={voiceJobActive}>Regenerate</Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function FidelityTab({ scene }) {
    return (
        <div className="space-y-3 text-xs">
            <div className="flex items-center gap-2">
                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${RISK_CLASS[scene.fidelity_risk]}`} data-testid="fidelity-risk-chip">
                    {scene.fidelity_risk} risk
                </span>
                {scene.fidelity_risk !== "LOW" && <AlertTriangle size={14} className="text-amber-600" />}
            </div>
            {(scene.fidelity_reasons || []).length > 0 && (
                <ul data-testid="fidelity-reasons" className="list-disc pl-5 space-y-1 text-[11px] text-neutral-600">
                    {scene.fidelity_reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
            )}
            <div>
                <Label className="text-xs mb-1 block">Narration sources</Label>
                {(scene.narration_source_refs || []).length === 0 ? (
                    <div className="text-[11px] text-neutral-400">— none —</div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {scene.narration_source_refs.map((r, i) => (
                            <span key={i} data-testid={`narration-ref-${i}`} className="inline-flex items-center rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] mono">
                                {r.kind}:{r.id.slice(0, 8)}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            <div>
                <Label className="text-xs mb-1 block">Technical parameters (carried from source step)</Label>
                {(scene.technical_parameters || []).length === 0 ? (
                    <div className="text-[11px] text-neutral-400">— none —</div>
                ) : (
                    <div className="rounded-md border border-neutral-200 overflow-hidden">
                        <table className="w-full text-[11px]">
                            <tbody>
                                {scene.technical_parameters.map((p, i) => (
                                    <tr key={i} className="border-t border-neutral-100">
                                        <td className="px-2 py-1 mono text-neutral-500">{p.name}</td>
                                        <td className="px-2 py-1 mono">{p.value ?? "—"}</td>
                                        <td className="px-2 py-1 mono">{p.unit ?? ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// SimpleSceneList — plain-English vertical scene cards for the layman admin.
// Each card shows a thumbnail placeholder, a one-line caption of what the
// trainer will say, and three actions: Approve · Edit text · Re-do. The
// current advanced surface stays intact behind the "Advanced" accordion below.
// ---------------------------------------------------------------------------
function SimpleSceneList({ scenes, moduleId, onChanged, onUpdateScene }) {
    const [editing, setEditing] = useState(null);
    const [editText, setEditText] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [onlyReview, setOnlyReview] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [previewScene, setPreviewScene] = useState(null);
    const [aiScene, setAiScene] = useState(null);
    // Approval state hydrated from `scenes[].approval_state` (persisted
    // in DB). Toggling calls POST /scenes/{id}/(un)approve so the write
    // survives reload. Optimistic local mirror keeps the click snappy.
    const [approved, setApproved] = useState(() => {
        const map = {};
        (scenes || []).forEach((s) => {
            if ((s.approval_state || "").toUpperCase() === "APPROVED") map[s.id] = true;
        });
        return map;
    });
    useEffect(() => {
        const map = {};
        (scenes || []).forEach((s) => {
            if ((s.approval_state || "").toUpperCase() === "APPROVED") map[s.id] = true;
        });
        setApproved(map);
    }, [scenes]);
    const approvedCount = Object.values(approved).filter(Boolean).length;
    const toggleApprove = async (sc) => {
        const wasApproved = !!approved[sc.id];
        // Optimistic — flip local state first
        setApproved((a) => ({ ...a, [sc.id]: !wasApproved }));
        try {
            if (wasApproved) {
                await api.post(`/scenes/${sc.id}/unapprove`);
                toast.success(`Scene ${sc.scene_number} approval cleared`);
            } else {
                await api.post(`/scenes/${sc.id}/approve`);
                toast.success(`Scene ${sc.scene_number} approved`);
            }
        } catch (e) {
            // Roll back optimistic change on failure
            setApproved((a) => ({ ...a, [sc.id]: wasApproved }));
            toast.error(formatApiError(e));
        }
    };

    if (!scenes?.length) return null;

    const openEdit = (sc) => {
        setEditing(sc);
        setEditText(sc.voiceover || sc.trainer_dialogue || "");
    };
    const saveEdit = async () => {
        if (!editing) return;
        setBusyId(editing.id);
        const field = editing.trainer_dialogue !== undefined && editing.trainer_dialogue !== null
            ? "trainer_dialogue" : "voiceover";
        const ok = await onUpdateScene(editing.id, { [field]: editText });
        setBusyId(null);
        if (ok !== false) setEditing(null);
    };
    const remaining = scenes.length - approvedCount;
    const approveAll = () => setConfirm({
        title: approvedCount === 0 ? "Approve all scenes?" : `Approve the remaining ${remaining} scenes?`,
        okLabel: "Approve", free: true,
        description: "Approving doesn't build the video. You choose when to make it.",
        onConfirm: async () => {
            try {
                const { data } = await api.post(`/modules/${moduleId}/scenes/approve-all`, {});
                toast.success(`${data.approved_now} scene${data.approved_now === 1 ? "" : "s"} approved · ${data.scenes_approved} of ${data.scenes_total} approved`, { duration: 8000 });
                if (data.failed?.length) toast.error(`${data.failed.length} scene(s) couldn't be approved`);
            } catch (e) { toast.error(formatApiError(e)); }
            onChanged && onChanged();
        },
    });
    const shown = onlyReview ? scenes.filter((s) => !approved[s.id]) : scenes;

    return (
        <div data-testid="simple-scene-list" className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="text-[11px] uppercase tracking-widest text-neutral-400" data-testid="scene-review-counter">
                    Your scenes · {approvedCount} of {scenes.length} approved
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-neutral-600" data-testid="simple-needs-review-filter">
                        <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} data-testid="simple-needs-review-filter-checkbox" />
                        Show only needs review
                    </label>
                    <span className="text-xs text-neutral-400" data-testid="simple-scene-showing">Showing {shown.length} of {scenes.length}</span>
                    {remaining > 0 && (
                        <Button size="sm" onClick={approveAll} data-testid="simple-approve-all-btn" className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800">
                            <Check size={13} className="mr-1" /> {approvedCount === 0 ? "Approve all" : `Approve remaining ${remaining}`}
                        </Button>
                    )}
                </div>
            </div>

            {shown.map((sc) => {
                const caption = (sc.trainer_dialogue || sc.voiceover || sc.visual_description || "").trim();
                const preview = caption.slice(0, 140);
                const isBusy = busyId === sc.id;
                const isApproved = !!approved[sc.id];
                const custom = !!sc.custom_prompt || !!sc.voice_override_voice_id;
                return (
                    <div
                        key={sc.id}
                        data-testid={`simple-scene-${sc.scene_number}`}
                        className={`rounded-2xl border px-4 py-3 flex items-start gap-4 transition-colors ${
                            isApproved ? "border-emerald-100 bg-emerald-50/30" : "border-neutral-200 bg-white"
                        }`}
                    >
                        <div className="w-20 aspect-video rounded-lg bg-neutral-100 shrink-0 flex items-center justify-center overflow-hidden">
                            <Film size={16} className="text-neutral-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="mb-0.5 flex items-center gap-1.5">
                                <span className="text-[11px] uppercase tracking-widest text-neutral-400">Scene {sc.scene_number}</span>
                                <SceneStatus scene={{ ...sc, approval_state: isApproved ? "APPROVED" : null }} />
                                {custom && <span data-testid={`simple-scene-custom-${sc.scene_number}`} title={sc.custom_prompt || "Custom voice"} className="inline-flex rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">Custom</span>}
                            </div>
                            {editing?.id === sc.id ? (
                                <>
                                    <textarea
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        rows={3}
                                        className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
                                        data-testid={`simple-scene-edit-${sc.scene_number}`}
                                    />
                                    <div className="mt-2 flex gap-2">
                                        <Button
                                            size="sm"
                                            onClick={saveEdit}
                                            disabled={isBusy}
                                            className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800"
                                            data-testid={`simple-scene-save-${sc.scene_number}`}
                                        >Save</Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-sm text-neutral-700 line-clamp-2">
                                    {preview || <span className="text-neutral-400 italic">No caption yet</span>}
                                </div>
                            )}
                        </div>
                        {editing?.id !== sc.id && (
                            <div className="flex gap-1 shrink-0">
                                <Button
                                    size="sm"
                                    variant={isApproved ? "default" : "outline"}
                                    onClick={() => toggleApprove(sc)}
                                    data-testid={`simple-scene-approve-${sc.scene_number}`}
                                    className={isApproved ? "rounded-full bg-emerald-600 text-white hover:bg-emerald-700" : "rounded-full"}
                                >
                                    <Check size={13} className="mr-1" /> {isApproved ? "Approved" : "Approve"}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEdit(sc)}
                                    data-testid={`simple-scene-edit-btn-${sc.scene_number}`}
                                    className="rounded-full text-neutral-600"
                                >Edit text</Button>
                                <div className="flex items-center px-1" data-testid={`simple-scene-menu-wrap-${sc.scene_number}`}>
                                    <SceneMenu scene={sc} moduleId={moduleId} onChanged={onChanged} onPreview={setPreviewScene} onFixWithAI={setAiScene} />
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
            <CostConfirm confirm={confirm} onClose={() => setConfirm(null)} />
            <ScenePreviewDialog scene={previewScene} moduleId={moduleId} onClose={() => setPreviewScene(null)} onChanged={onChanged} />
            <AiFixPanel open={!!aiScene} onOpenChange={(o) => !o && setAiScene(null)} moduleId={moduleId} scene={aiScene} onApplied={onChanged} />
        </div>
    );
}
